import { useEffect, useCallback, useState } from 'react';
import { useJournal, calculateStreak } from '../hooks/useJournal';
import { useBadges } from '../hooks/useBadges';
import { useDesafios } from '../hooks/useDesafios';

// Componentes existentes
import StreakCounter from '../components/journal/StreakCounter';
import MoodPicker from '../components/journal/MoodPicker';
import ReflectionInput from '../components/journal/ReflectionInput';
import BadgeCelebrationModal from '../components/gamification/BadgeCelebrationModal';
import PontosToast from '../components/gamification/PontosToast';
import BadgesHome from '../components/gamification/BadgesHome';
import OracleModal from '../components/OracleModal';
import OraculoErrorBoundary from '../components/OraculoErrorBoundary';
import ModalCompartilhar from '../components/community/ModalCompartilhar';
import MicroCelebracao from '../components/journal/MicroCelebracao';
import DesafioAtivoCard from '../components/desafios/DesafioAtivoCard';
import ModalCelebracao from '../components/desafios/ModalCelebracao';

import WeekPresenceCard from '../components/journal/WeekPresenceCard'
import MemoryCard from '../components/journal/MemoryCard'
import JornadaCardHome from '../components/jornada/JornadaCardHome'
import { useJornada } from '../hooks/useJornada'

// Novos componentes da home
import CarrosselDestaque from '../components/home/CarrosselDestaque';
import ShortsDoClube from '../components/home/ShortsDoClube';
import AgendaDoMes from '../components/home/AgendaDoMes';

import { getPromptDoDia, getDiaAtual } from '../utils/desafios';

export default function HomePage({ user, profile, needsSetup, onNavigateToProfile, onProfileRefresh, onNavigate, homeSlides, homeShorts, homeAgenda }) {
  // ── Custom hooks ────────────────────────────────────────
  const {
    logs, streak, hasCheckedToday, todayNote, todayMood, todayMoodWord,
    fetchData, checkIn, saveSentimento, saveNote
  } = useJournal(user.id);

  const { earnedBadges, newlyEarned, fetchBadges, evaluateBadges, evaluateChallengeBadges, evaluateShareBadges, dismissNewBadges } = useBadges(user.id);
  const { desafioAtivo, desafiosConcluidos, fetchDesafios, concluirDesafio } = useDesafios(user.id);
  const { jornada } = useJornada();

  // ── Estado local ─────────────────────────────────────────
  const [mostrarOraculo, setMostrarOraculo] = useState(false);
  const [notaParaCompartilhar, setNotaParaCompartilhar] = useState(null);
  const [mostrarCelebracao, setMostrarCelebracao] = useState(false);
  const [mostrarMicroCelebracao, setMostrarMicroCelebracao] = useState(false);
  const [dadosCarregados, setDadosCarregados] = useState(false);
  const [pontosToast, setPontosToast] = useState(null);

  // Score selecionado localmente — atualiza imediatamente para feedback visual
  const [selectedWord, setSelectedWord] = useState(null);

  const MOOD_LABELS = { 1: 'Difícil', 2: 'Neutro', 3: 'Ok', 4: 'Bem', 5: 'Ótimo' };


  // ── Callbacks ───────────────────────────────────────────
  const handleSaveNote = useCallback(async (note) => {
    const result = await saveNote(note);
    if (!result.error) {
      const updatedLogs = await fetchData(user.id);
      await evaluateBadges(updatedLogs);
      if (note && note.trim().length > 0) {
        setNotaParaCompartilhar(note.trim());
        setMostrarMicroCelebracao(true);
      }
    }
    return result;
  }, [saveNote, fetchData, evaluateBadges, user.id]);

  const handleFecharCelebracao = useCallback(async (parametroDepois) => {
    setMostrarCelebracao(false);
    if (desafioAtivo) {
      await concluirDesafio(parametroDepois || null);
      await evaluateChallengeBadges(
        desafioAtivo.desafio_id,
        (desafiosConcluidos?.length || 0) + 1
      );
    }
  }, [desafioAtivo, concluirDesafio, evaluateChallengeBadges, desafiosConcluidos]);

  const buildPontosToast = useCallback(function(newStreak) {
    var MILESTONES = { 7: 20, 14: 40, 30: 100, 100: 200 };
    var streakBonus = MILESTONES[newStreak] || 0;
    var monthBonus = new Date().getDate() === 1 ? 15 : 0;
    var total = 10 + streakBonus + monthBonus;
    var label = '';
    if (streakBonus > 0) label = newStreak + ' dias seguidos! 🔥';
    else if (monthBonus > 0) label = 'primeiro dia do mês!';
    return { pontos: total, label };
  }, []);

  const handleReceberOraculo = useCallback(async () => {
    if (!hasCheckedToday) {
      await checkIn(null);
      const updatedLogs = await fetchData(user.id);
      await evaluateBadges(updatedLogs);
      const newStreak = calculateStreak(updatedLogs);
      setPontosToast(buildPontosToast(newStreak));
    }
  }, [hasCheckedToday, checkIn, fetchData, evaluateBadges, user.id, buildPontosToast]);

  const handleMood = useCallback(async (score) => {
    const word = MOOD_LABELS[score] || String(score);
    setSelectedWord(word);
    const wasChecked = hasCheckedToday;
    await saveSentimento(word, score);
    if (!wasChecked) {
      const updatedLogs = await fetchData(user.id);
      const newStreak = calculateStreak(updatedLogs);
      setPontosToast(buildPontosToast(newStreak));
    }
  }, [saveSentimento, hasCheckedToday, fetchData, user.id, buildPontosToast]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effects ─────────────────────────────────────────────
  useEffect(() => {
    const carregar = async () => {
      await fetchData(user.id);
      setDadosCarregados(true);
    };
    carregar();
    fetchBadges();
  }, [user.id]); // eslint-disable-line

  useEffect(() => {
    fetchDesafios();
  }, [user.id]); // eslint-disable-line

  useEffect(() => {
    if (dadosCarregados && !hasCheckedToday) {
      setMostrarOraculo(true);
    }
    if (todayMoodWord) setSelectedWord(todayMoodWord);
  }, [dadosCarregados, hasCheckedToday, todayMoodWord]);

  useEffect(() => {
    if (!desafioAtivo) return;
    const { concluido } = getDiaAtual(desafioAtivo.data_inicio, desafioAtivo.duracao);
    if (concluido) setMostrarCelebracao(true);
  }, [desafioAtivo]);

  // ── Derivações ──────────────────────────────────────────
  const displayName = profile?.display_name || user.email.split('@')[0];

  return (
    <>
      {/* ── Overlays & Modais ─────────────────────────────── */}
      {mostrarOraculo && (
        <OraculoErrorBoundary>
          <OracleModal
            onClose={() => setMostrarOraculo(false)}
            onReceberOraculo={handleReceberOraculo}
            userName={displayName}
          />
        </OraculoErrorBoundary>
      )}

      {mostrarMicroCelebracao && (
        <MicroCelebracao
          streak={streak}
          mood={todayMood}
          onFim={() => setMostrarMicroCelebracao(false)}
        />
      )}

      {pontosToast && (
        <PontosToast
          pontos={pontosToast.pontos}
          label={pontosToast.label}
          onDismiss={() => setPontosToast(null)}
        />
      )}

      {newlyEarned.length > 0 && (
        <BadgeCelebrationModal badges={newlyEarned} onDismiss={dismissNewBadges} />
      )}

      {/* ── Setup Banner ──────────────────────────────────── */}
      {needsSetup && (
        <div className="setup-banner">
          <p>Complete seu perfil para personalizar sua experiência.</p>
          <button className="btn btn-primary btn-sm" onClick={onNavigateToProfile}>
            Completar perfil
          </button>
        </div>
      )}

      {/* ── 1. Saudação ──────────────────────────────────── */}
      <div className="section home-greeting">
        <div className="home-greeting-text">
          <h1 style={{ marginBottom: 2, color: 'var(--navy)' }}>
            Olá, {displayName}!
          </h1>
          <p className="home-date">
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>

        {/* Streak em destaque logo abaixo da data */}
        <StreakCounter streak={streak} hero />

        {/* Jornada ao Oceano — substitui o humor picker quando ativa */}
        {jornada?.visible ? (
          <JornadaCardHome jornada={jornada} onNavigate={onNavigate} />
        ) : (
          <div className="sentimento-card">
            <MoodPicker
              value={todayMood}
              onChange={handleMood}
              disabled={!!selectedWord}
            />
          </div>
        )}

        {/* Semana atual */}
        <WeekPresenceCard
          logs={logs}
          onNavigateToProfile={() => onNavigateToProfile && onNavigateToProfile()}
        />

        {/* Memória — nesse dia X semanas atrás */}
        <MemoryCard logs={logs} userId={user.id} />

        {/* Reflexão diária — logo abaixo do neste dia */}
        {!desafioAtivo && (
          <>
            <h2 className="section-title">Um minuto para refletir</h2>
            <div className="section">
              <ReflectionInput
                initialNote={todayNote}
                onSave={handleSaveNote}
                prompt={getPromptDoDia()}
                lockAfterSave
              />
            </div>
          </>
        )}

        {/* Badges conquistadas */}
        <BadgesHome earnedBadges={earnedBadges} />
      </div>

      {/* ── 2. Suas participações ────────────────────────── */}
      {desafioAtivo && !mostrarCelebracao && (
        <>
          <h2 className="section-title">Suas participações</h2>
          <div className="section">
            <DesafioAtivoCard desafio={desafioAtivo} logs={logs} />
            {onNavigate && (
              <button
                className="btn btn-ghost desafio-reflexao-cta"
                onClick={() => onNavigate('detox')}
              >
                Fazer reflexão do desafio →
              </button>
            )}
          </div>
        </>
      )}

      {/* ── 5. Carrossel de destaques ─────────────────────── */}
      {homeSlides.length > 0 && (
        <>
          <h2 className="section-title">Destaques do Clube</h2>
          <CarrosselDestaque slides={homeSlides} />
        </>
      )}


      {/* ── 7. Shorts do clube ────────────────────────────── */}
      <ShortsDoClube shorts={homeShorts} />

      {/* ── 8. Agenda do mês ──────────────────────────────── */}
      <AgendaDoMes eventos={homeAgenda} />

      {/* ── Modais de conclusão ───────────────────────────── */}
      {notaParaCompartilhar && (
        <ModalCompartilhar
          nota={notaParaCompartilhar}
          desafioAtivo={desafioAtivo}
          prompt={getPromptDoDia()}
          userId={user?.id}
          onFechar={(shared) => {
            setNotaParaCompartilhar(null);
            if (shared) {
              evaluateShareBadges();
              setPontosToast({ pontos: 30, label: 'reflexão compartilhada!' });
            }
          }}
        />
      )}

      {mostrarCelebracao && desafioAtivo && (
        <ModalCelebracao
          desafio={desafioAtivo}
          conquista={earnedBadges.find(b => b.slug === 'desafio_1' || b.slug === 'desafio_gratidao')}
          onFechar={handleFecharCelebracao}
        />
      )}
    </>
  );
}
